import { useState, useEffect, useRef } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

export default function Contacts() {
  const { selectedBusiness } = useAuth();
  const bid = selectedBusiness?.id;
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [tags, setTags] = useState([]);
  const [selected, setSelected] = useState([]);
  const [showBulkTag, setShowBulkTag] = useState(false);
  const [bulkTagId, setBulkTagId] = useState('');
  const [bulkTagging, setBulkTagging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadTagId, setUploadTagId] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadFileName, setUploadFileName] = useState('');
  const [uploadMsg, setUploadMsg] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', phone: '', tag_id: '' });
  const [addMsg, setAddMsg] = useState('');
  const [modalTab, setModalTab] = useState('create');
  const [tagPanel, setTagPanel] = useState(null);
  const [contactTags, setContactTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => { if (bid) { loadTags(); loadContacts(); } }, [bid, page, filterTag]);

  const loadContacts = async () => {
    setLoading(true);
    try {
      let url = `/contacts?business_id=${bid}&page=${page}`;
      if (filterTag) url += `&tag_id=${filterTag}`;
      const r = await api.get(url);
      const data = r.data?.contacts || (Array.isArray(r.data) ? r.data : []);
      setContacts(data);
      setTotal(r.data?.total || data.length);
    } catch { setContacts([]); }
    setLoading(false);
  };

  const loadTags = async () => {
    try {
      const r = await api.get(`/tags?business_id=${bid}`);
      setTags(Array.isArray(r.data) ? r.data : []);
    } catch {}
  };

  const loadContactTags = async (contactId) => {
    try {
      const r = await api.get(`/tags/contact/${contactId}?business_id=${bid}`);
      setContactTags(Array.isArray(r.data) ? r.data.map(t => t.id) : []);
    } catch { setContactTags([]); }
  };

  const toggleTag = async (tagId, contactId) => {
    const hasTag = contactTags.includes(tagId);
    try {
      if (hasTag) {
        await api.delete(`/tags/assign/${contactId}/${tagId}`);
        setContactTags(prev => prev.filter(id => id !== tagId));
      } else {
        await api.post('/tags/assign', { contact_id: contactId, tag_id: tagId, business_id: bid });
        setContactTags(prev => [...prev, tagId]);
      }
      loadContacts();
    } catch {}
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadFile(file);
    setUploadFileName(file.name);
  };

  const uploadCSV = async () => {
    if (!uploadFile) return;
    setUploading(true); setUploadMsg('');
    const form = new FormData();
    form.append('file', uploadFile);
    form.append('business_id', bid);
    if (uploadTagId) form.append('tag_id', uploadTagId);
    try {
      const r = await api.post('/contacts/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadMsg(`✅ ${r.data.imported || 0} contacts imported`);
      setUploadFile(null); setUploadFileName('');
      loadContacts();
    } catch { setUploadMsg('❌ Upload failed'); }
    setUploading(false);
    setTimeout(() => setUploadMsg(''), 3000);
  };

  const addContact = async () => {
    if (!addForm.phone) { setAddMsg('Phone required'); return; }
    const cleanPhone = addForm.phone.replace(/[^0-9]/g, '');
    const existing = contacts.find(c => c.phone && c.phone.replace(/[^0-9]/g, '').endsWith(cleanPhone.slice(-10)));
    if (existing) { setAddMsg('⚠️ Already exists: ' + (existing.name || existing.phone)); return; }
    try {
      await api.post('/contacts', { ...addForm, business_id: bid });
      setAddMsg('✅ Contact added!');
      setAddForm({ name: '', phone: '', tag_id: '' });
      loadContacts();
      setTimeout(() => { setShowAdd(false); setAddMsg(''); }, 1200);
    } catch (e) { setAddMsg('❌ ' + (e.response?.data?.error || 'Failed')); }
  };

  const deleteSelected = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Delete ${selected.length} contact(s)?`)) return;
    for (const id of selected) {
      await api.delete(`/contacts/${id}?business_id=${bid}`).catch(() => {});
    }
    setSelected([]);
    loadContacts();
  };

  const getContrast = (hex) => {
    if (!hex || hex.length < 7) return '#fff';
    const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
    return (0.299*r + 0.587*g + 0.114*b)/255 > 0.55 ? '#1a1a1a' : '#ffffff';
  };

  const filtered = contacts.filter(c => {
    if (fromDate && c.created_at && new Date(c.created_at) < new Date(fromDate)) return false;
    if (toDate && c.created_at && new Date(c.created_at) > new Date(toDate + 'T23:59:59')) return false;
    return true;
  }).filter(c =>
    (c.name || c.phone || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.phone || '').includes(search)
  );

  const toggleSelect = (id) => setSelected(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  const selectAll = () => selected.length === filtered.length ? setSelected([]) : setSelected(filtered.map(c => c.id));

  const bulkAssignTag = async () => {
    if (!bulkTagId) return;
    setBulkTagging(true);
    for (const id of selected) {
      await api.post('/tags/assign', { contact_id: id, tag_id: bulkTagId, business_id: bid }).catch(() => {});
    }
    setBulkTagging(false);
    setShowBulkTag(false);
    setBulkTagId('');
    loadContacts();
    alert(`Tag assigned to ${selected.length} contacts!`);
  };

  const inp = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' };

  const exportCSV = () => {
    const rows = [['Name','Phone','Tags','Created At']];
    filtered.forEach(c => {
      rows.push([c.name||'', c.phone||'', c.tag_names||'', c.created_at ? new Date(c.created_at).toLocaleDateString('en-GB') : '']);
    });
    const csv = rows.map(r => r.map(v => '"'+String(v).replace(/"/g,'""')+'"').join(',')).join('\n');
    const blob = new Blob([csv], {type:'text/csv'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href=url; a.download='contacts.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  if (!bid) return <div style={{ padding: 24, color: '#9ca3af' }}>Please select a business first.</div>;

  return (
    <div style={{ padding: '24px 28px', fontFamily: 'DM Sans, sans-serif', background: '#f4f6f9', minHeight: '100vh' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: '#111', margin: 0 }}>Contacts</h2>
        <div style={{ display: 'flex', gap: 10 }}>
          <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={async (e) => { const f = e.target.files[0]; if(!f) return; setUploading(true); const form = new FormData(); form.append('file', f); form.append('business_id', bid); if(filterTag) form.append('tag_id', filterTag); try { const r = await api.post('/contacts/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } }); setUploadMsg(`✅ ${r.data.imported || 0} contacts imported`); loadContacts(); } catch { setUploadMsg('❌ Upload failed'); } setUploading(false); setTimeout(() => setUploadMsg(''), 3000); }} />
          {uploadMsg && <span style={{ fontSize: 15, color: uploadMsg.includes('✅') ? '#16a34a' : '#ef4444', alignSelf: 'center' }}>{uploadMsg}</span>}
          {selected.length > 0 && (
            <>
            <button onClick={deleteSelected} style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#fee2e2', color: '#dc2626', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
              Delete ({selected.length})
            </button>
            <button onClick={() => setShowBulkTag(true)} style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#eff6ff', color: '#2563eb', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
              🏷️ Tag ({selected.length})
            </button>
            </>
          )}
          <button onClick={exportCSV} style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', fontWeight: 600, fontSize: 15, cursor: 'pointer', color: '#374151' }}>
            📥 Export CSV
          </button>
          <button onClick={() => fileRef.current.click()} disabled={uploading} style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', fontWeight: 600, fontSize: 15, cursor: 'pointer', color: '#374151' }}>
            {uploading ? 'Uploading...' : '📤 Upload CSV'}
          </button>
          <button onClick={() => { setShowAdd(true); setAddMsg(''); }} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
            + Contacts
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '14px 20px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          style={{ ...inp, width: 180, padding: '8px 12px' }}
          placeholder="Search contacts..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
          style={{ ...inp, width: 140, padding: '8px 12px' }} placeholder="From Date" />
        <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
          style={{ ...inp, width: 140, padding: '8px 12px' }} placeholder="To Date" />
        <select
          value={filterTag}
          onChange={e => { setFilterTag(e.target.value); setPage(1); }}
          style={{ ...inp, width: 180, padding: '8px 12px' }}
        >
          <option value="">All Tags</option>
          {tags.map(t => <option key={t.id} value={t.id}>🏷️ {t.name}</option>)}
        </select>
        <span style={{ fontSize: 15, color: '#9ca3af', marginLeft: 'auto' }}>Total: <strong>{total}</strong></span>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              <th style={{ padding: '12px 16px', width: 40 }}>
                <input type="checkbox" checked={selected.length === filtered.length && filtered.length > 0}
                  onChange={selectAll} style={{ accentColor: '#16a34a', cursor: 'pointer' }} />
              </th>
              {['Name', 'Phone Number', 'Tags', 'Created At', 'Action'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: '#6b7280', fontWeight: 600, fontSize: 15, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #f3f4f6' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: '48px', textAlign: 'center', color: '#9ca3af' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>👥</div>
                No contacts found
              </td></tr>
            ) : filtered.map((c, i) => {
              const tagNames = c.tag_names ? c.tag_names.split(',') : [];
              const tagColors = c.tag_colors ? c.tag_colors.split(',') : [];
              return (
                <tr key={c.id || i} style={{ borderBottom: '1px solid #f3f4f6', background: selected.includes(c.id) ? '#f0fdf4' : 'transparent' }}>
                  <td style={{ padding: '12px 16px' }}>
                    <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggleSelect(c.id)} style={{ accentColor: '#16a34a', cursor: 'pointer' }} />
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#25D366', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, flexShrink: 0 }}>
                        {(c.name || c.phone || '?')[0].toUpperCase()}
                      </div>
                      <span style={{ fontWeight: 600, color: '#111' }}>{c.name || '—'}</span>
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', color: '#16a34a', fontWeight: 600 }}>{c.phone}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
                      {tagNames.length > 0 && tagNames[0] ? tagNames.map((name, idx) => (
                        <span key={idx} style={{
                          background: tagColors[idx] || '#16a34a',
                          color: getContrast(tagColors[idx] || '#16a34a'),
                          borderRadius: 5, padding: '2px 8px', fontSize: 15, fontWeight: 600,
                        }}>{name}</span>
                      )) : <span style={{ color: '#9ca3af', fontSize: 12 }}>No Tags</span>}
                      <button
                        onClick={() => { setTagPanel(tagPanel === c.id ? null : c.id); loadContactTags(c.id); }}
                        style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: 5, padding: '2px 8px', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}
                      >+ Tag</button>
                    </div>
                    {/* Inline Tag Panel */}
                    {tagPanel === c.id && (
                      <div style={{ marginTop: 8, padding: '10px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {tags.length === 0 ? <span style={{ fontSize: 14, color: '#9ca3af' }}>No tags. Create tags first.</span>
                          : tags.map(tag => {
                            const assigned = contactTags.includes(tag.id);
                            return (
                              <button key={tag.id} onClick={() => toggleTag(tag.id, c.id)}
                                style={{ background: assigned ? (tag.color || '#16a34a') : '#fff', color: assigned ? getContrast(tag.color || '#16a34a') : '#374151', border: `1px solid ${assigned ? (tag.color || '#16a34a') : '#e5e7eb'}`, borderRadius: 6, padding: '4px 10px', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                                {assigned ? '✓ ' : ''}{tag.name}
                                {assigned && <span style={{ marginLeft: 2, fontWeight: 700, fontSize: 14 }}>×</span>}
                              </button>
                            );
                          })}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', color: '#9ca3af', fontSize: 12 }}>
                    {c.created_at ? new Date(c.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <button onClick={() => { if (window.confirm('Delete this contact?')) api.delete(`/contacts/${c.id}?business_id=${bid}`).then(loadContacts); }}
                      style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 6, padding: '5px 10px', fontSize: 14, cursor: 'pointer' }}>🗑</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Pagination */}
        {total > 50 && (
          <div style={{ padding: '14px 20px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'center', gap: 8 }}>
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #e5e7eb', background: page === 1 ? '#f9fafb' : '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer', fontSize: 13 }}>← Prev</button>
            <span style={{ padding: '6px 14px', fontSize: 15, color: '#374151' }}>Page {page}</span>
            <button disabled={page * 50 >= total} onClick={() => setPage(p => p + 1)} style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #e5e7eb', background: page * 50 >= total ? '#f9fafb' : '#fff', cursor: page * 50 >= total ? 'not-allowed' : 'pointer', fontSize: 13 }}>Next →</button>
          </div>
        )}
      </div>

      {/* Add Contact Modal */}
      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => e.target === e.currentTarget && setShowAdd(false)}>
          <div style={{ background: '#fff', borderRadius: 14, padding: '28px', width: '90%', maxWidth: 480, boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 17, fontWeight: 700 }}>Add Contacts</h3>
            
            {/* Tabs */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid #e5e7eb' }}>
              <button onClick={() => setModalTab('create')} style={{ padding: '8px 20px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: modalTab === 'create' ? '#16a34a' : '#6b7280', borderBottom: modalTab === 'create' ? '2px solid #16a34a' : '2px solid transparent', marginBottom: -2 }}>
                Create New Contact
              </button>
              <button onClick={() => setModalTab('upload')} style={{ padding: '8px 20px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: modalTab === 'upload' ? '#16a34a' : '#6b7280', borderBottom: modalTab === 'upload' ? '2px solid #16a34a' : '2px solid transparent', marginBottom: -2 }}>
                Upload via Excel/CSV
              </button>
            </div>

            {modalTab === 'create' && (<>
              <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 5, display: 'block' }}>Customer Name</label>
                  <input style={inp} placeholder="Enter Contact Name" value={addForm.name} onChange={e => setAddForm({ ...addForm, name: e.target.value })} />
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 5, display: 'block' }}>Phone Number <span style={{ color: '#ef4444' }}>*</span></label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select style={{ ...inp, width: 120, flexShrink: 0 }} value={addForm.countryCode || '91'} onChange={e => setAddForm({ ...addForm, countryCode: e.target.value })}>
                    <option value="91">🇮🇳 +91</option>
                    <option value="1">🇺🇸 +1</option>
                    <option value="44">🇬🇧 +44</option>
                    <option value="971">🇦🇪 +971</option>
                    <option value="65">🇸🇬 +65</option>
                    <option value="60">🇲🇾 +60</option>
                    <option value="61">🇦🇺 +61</option>
                    <option value="49">🇩🇪 +49</option>
                    <option value="33">🇫🇷 +33</option>
                    <option value="86">🇨🇳 +86</option>
                  </select>
                  <input style={inp} placeholder="10 digit number" maxLength={10} value={addForm.phoneDigits || ''} onChange={e => { const val = e.target.value.replace(/[^0-9]/g, '').slice(0,10); setAddForm({ ...addForm, phoneDigits: val, phone: (addForm.countryCode || '91') + val }); }} />
                </div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>Enter 10 digit number only (country code auto added)</div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 5, display: 'block' }}>Select Tag</label>
                <select style={inp} value={addForm.tag_id} onChange={e => setAddForm({ ...addForm, tag_id: e.target.value })}>
                  <option value="">-- Choose a tag --</option>
                  {tags.map(t => <option key={t.id} value={t.id}>🏷️ {t.name}</option>)}
                </select>
              </div>
              {addMsg && <div style={{ marginBottom: 14, fontSize: 15, color: addMsg.includes('✅') ? '#16a34a' : '#ef4444', fontWeight: 600 }}>{addMsg}</div>}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button onClick={() => setShowAdd(false)} style={{ padding: '10px 22px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#374151' }}>Cancel</button>
                <button onClick={addContact} style={{ padding: '10px 22px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>Submit</button>
              </div>
            </>)}

            {modalTab === 'upload' && (<>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <a href="#" onClick={e => { e.preventDefault(); const csv = 'name,phone,Note\nJohn Doe,="919940407341","Accepted headers - Name: Name/Customer Name/Client Name/Full Name; Phone: Phone/Mobile/Contact/Cell/Tel/WhatsApp Number"'; const b = new Blob([csv],{type:'text/csv'}); const u = URL.createObjectURL(b); const a = document.createElement('a'); a.href=u; a.download='sample_contacts.csv'; a.click(); }} style={{ fontSize: 15, color: '#16a34a', fontWeight: 600, textDecoration: 'none' }}>
                  — Download Sample File
                </a>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>Choose File</label>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={handleFileSelect} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button onClick={() => fileRef.current.click()} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #d1d5db', background: '#f9fafb', cursor: 'pointer', fontSize: 15, fontWeight: 600, color: '#374151', flexShrink: 0 }}>Choose File</button>
                  <span style={{ fontSize: 15, color: uploadFileName ? '#16a34a' : '#9ca3af' }}>{uploadFileName || 'No file chosen'}</span>
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>Select Tag</label>
                <select style={inp} value={uploadTagId} onChange={e => setUploadTagId(e.target.value)}>
                  <option value="">-- Choose a tag --</option>
                  {tags.map(t => <option key={t.id} value={t.id}>🏷️ {t.name}</option>)}
                </select>
              </div>
              {uploadMsg && <div style={{ marginBottom: 14, fontSize: 15, fontWeight: 600, color: uploadMsg.includes('✅') ? '#16a34a' : '#ef4444' }}>{uploadMsg}</div>}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button onClick={() => { setShowAdd(false); setUploadTagId(''); }} style={{ padding: '10px 22px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#374151' }}>Cancel</button>
                <button onClick={uploadCSV} disabled={uploading || !uploadFile} style={{ padding: '10px 22px', borderRadius: 8, border: 'none', background: uploadFile ? '#16a34a' : '#9ca3af', color: '#fff', cursor: uploadFile ? 'pointer' : 'not-allowed', fontSize: 14, fontWeight: 700 }}>{uploading ? 'Uploading...' : 'Submit'}</button>
              </div>
            </>)}
          </div>
        </div>
      )}
      {showBulkTag && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 400, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>🏷️ Assign Tag to {selected.length} contacts</h2>
            <select value={bulkTagId} onChange={e => setBulkTagId(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e0e0e0', borderRadius: 8, fontSize: 14, marginBottom: 16, outline: 'none' }}>
              <option value="">Select a tag...</option>
              {tags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowBulkTag(false)} style={{ flex: 1, padding: '10px', background: '#f0f2f5', color: '#333', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={bulkAssignTag} disabled={!bulkTagId || bulkTagging} style={{ flex: 1, padding: '10px', background: bulkTagId ? '#2563eb' : '#ccc', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: bulkTagId ? 'pointer' : 'not-allowed' }}>
                {bulkTagging ? 'Assigning...' : 'Assign Tag'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
